// src/frontend/src/types/simulation.ts
import { User } from './index';

// Stakeholder Types
export enum StakeholderType {
  CLIENT = 'client',
  EXECUTIVE_SPONSOR = 'executive_sponsor',
  TEAM_MEMBER = 'team_member',
  VENDOR = 'vendor',
  SUBJECT_MATTER_EXPERT = 'subject_matter_expert',
  END_USER = 'end_user',
  REGULATORY = 'regulatory',
  TECHNICAL_LEAD = 'technical_lead'
}

export enum CommunicationStyle {
  DIRECT = 'direct',
  ANALYTICAL = 'analytical',
  FRIENDLY = 'friendly',
  DOMINANT = 'dominant',
  PASSIVE = 'passive',
  DETAIL_ORIENTED = 'detail_oriented',
  BIG_PICTURE = 'big_picture',
  EMOTIONAL = 'emotional'
}

export enum Difficulty {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard'
}

export enum ChallengeType {
  SCOPE_CREEP = 'scope_creep',
  BUDGET_CONSTRAINTS = 'budget_constraints',
  TIMELINE_PRESSURE = 'timeline_pressure',
  QUALITY_CONCERNS = 'quality_concerns',
  COMMUNICATION_ISSUES = 'communication_issues',
  TECHNICAL_COMPLEXITY = 'technical_complexity',
  RESOURCE_LIMITATIONS = 'resource_limitations',
  COMPETING_PRIORITIES = 'competing_priorities',
  RESISTANT_TO_CHANGE = 'resistant_to_change',
  UNREALISTIC_EXPECTATIONS = 'unrealistic_expectations'
}

export interface StakeholderPersona {
  _id: string;
  name: string;
  type: StakeholderType;
  description: string;
  backstory: string;
  communicationStyle: CommunicationStyle;
  primaryChallenges: ChallengeType[];
  difficulty: Difficulty;
  traits: string[];
  createdBy: User | string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

// Scenario Types
export enum ScenarioCategory {
  PROJECT_INITIATION = 'project_initiation',
  REQUIREMENTS_GATHERING = 'requirements_gathering',
  SCOPE_MANAGEMENT = 'scope_management',
  TIMELINE_NEGOTIATION = 'timeline_negotiation',
  BUDGET_DISCUSSION = 'budget_discussion',
  CONFLICT_RESOLUTION = 'conflict_resolution',
  CHANGE_MANAGEMENT = 'change_management',
  RISK_MITIGATION = 'risk_mitigation',
  STATUS_REPORTING = 'status_reporting',
  RESOURCE_ALLOCATION = 'resource_allocation'
}

export enum LearningObjective {
  STAKEHOLDER_COMMUNICATION = 'stakeholder_communication',
  CONFLICT_RESOLUTION = 'conflict_resolution',
  NEGOTIATION_SKILLS = 'negotiation_skills',
  EXPECTATION_MANAGEMENT = 'expectation_management',
  PROBLEM_SOLVING = 'problem_solving',
  ACTIVE_LISTENING = 'active_listening',
  PERSUASION = 'persuasion',
  DOCUMENTATION = 'documentation',
  EMPATHY_BUILDING = 'empathy_building',
  LEADERSHIP = 'leadership'
}

export interface SimulationScenario {
  _id: string;
  title: string;
  description: string;
  projectContext: string;
  category: ScenarioCategory;
  stakeholderType: StakeholderType;
  stakeholderPersona?: StakeholderPersona | string;
  challengeTypes: ChallengeType[];
  learningObjectives: LearningObjective[];
  difficulty: Difficulty;
  initialPrompt: string;
  suggestedApproaches: string;
  evaluationCriteria: string;
  createdBy: User | string;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

// Conversation Types
export enum SpeakerRole {
  STUDENT = 'student',
  STAKEHOLDER = 'stakeholder'
}

export enum ConversationStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ABANDONED = 'abandoned'
}

export interface Message {
  role: SpeakerRole;
  content: string;
  timestamp: string;
}

export interface FeedbackPoint {
  type: 'positive' | 'negative' | 'suggestion';
  message: string;
  relatedMessages?: number[];
}

export interface ConversationFeedback {
  overallScore: number;
  strengths: string[];
  areasForImprovement: string[];
  keyInsights: string;
  specificFeedback: FeedbackPoint[];
  suggestedApproachForNextTime: string;
}

export interface Conversation {
  _id: string;
  user: User | string;
  scenario: SimulationScenario | string;
  stakeholderPersona: StakeholderPersona | string;
  status: ConversationStatus;
  messages: Message[];
  startedAt: string;
  completedAt?: string;
  feedback?: ConversationFeedback;
  createdAt: string;
  updatedAt: string;
}
